import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  MutationCreateJobProfileArgs,
  MutationUpdateJobProfileArgs,
  JobProfilesModel,
  CountryModel,
  JobProfileModel,
  JobProfileCreateModelInput,
  JobProfileUpdateModelInput,
  JobProfileQueryModelInput,
  PageModelInput
} from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import useSystemLoading from 'src/utils/hooks/useSystemLoading';
import {
  CREATE_ENGAGEMENTS_JOB_PROFILE,
  GET_ENGAGEMENTS_JOB_PROFILES,
  REMOVE_JOB_PROFILE,
  UPDATE_ENGAGEMENTS_JOB_PROFILE
} from './jobProfiles.mutations';
import { useJobProfilesIntl } from './translations/useJobProfilesIntl';

export type JobProfileQuery = DataTableTypes.OnVariablesChanged<
  { search: string; classificationAssignable: boolean },
  'displayName'
>;

export const useJobProfileData = () => {
  const intl = useJobProfilesIntl();
  const { showSuccess, showError } = useSnackbar();
  const [variables, setVariables] = useState<JobProfileQuery | undefined>({
    page: { offset: 0, limit: 40 },
    query: { classificationAssignable: false, search: '' }
  });

  const [getProfiles, { data: jobProfiles, loading: isGettingProfiles, refetch }] = useLazyQuery<
    {
      jobProfiles: JobProfilesModel;
    },
    DeepPartial<{ query: JobProfileQueryModelInput; page: PageModelInput }>
  >(GET_ENGAGEMENTS_JOB_PROFILES);

  useEffect(() => {
    getProfiles({ variables: { query: variables?.query, page: variables?.page } });
  }, [getProfiles, variables?.page, variables?.query]);

  const [createJobProfile, { loading: createLoading }] = useMutation<
    { jobProfile: JobProfileModel },
    { jobProfile?: DeepPartial<JobProfileCreateModelInput> }
  >(CREATE_ENGAGEMENTS_JOB_PROFILE, { onError: () => showError(intl.failedToCreateJobProfile) });

  const [updateJobProfile, { loading: updateLoading }] = useMutation<
    { jobProfile: JobProfileModel },
    { jobProfile?: DeepPartial<JobProfileUpdateModelInput> }
  >(UPDATE_ENGAGEMENTS_JOB_PROFILE, { onError: () => showError(intl.failedToUpdateJobProfile) });

  const [removeJobProfile, { loading: removeLoading }] = useMutation<{ deleteJobProfile: boolean }>(
    REMOVE_JOB_PROFILE,
    { onError: (e) => showError(e.message) }
  );

  useSystemLoading(createLoading || updateLoading || removeLoading || isGettingProfiles);

  const update = useCallback(
    async (params: DeepPartial<MutationUpdateJobProfileArgs>) => {
      const clonedData = structuredClone(params) as MutationUpdateJobProfileArgs;

      if (!clonedData.jobProfile.jobFamily?.uid) clonedData.jobProfile.jobFamily = null as any;

      const data = await updateJobProfile({
        variables: clonedData
      });
      showSuccess(intl.jobProfileUpdatedSuccessfully);
      return data.data?.jobProfile;
    },
    [intl.jobProfileUpdatedSuccessfully, showSuccess, updateJobProfile]
  );

  const create = useCallback(
    async (params: DeepPartial<MutationCreateJobProfileArgs>) => {
      const created = await createJobProfile({
        variables: structuredClone(params)
      });
      showSuccess(intl.newJobProfileCreatedSuccessfully);
      return created.data?.jobProfile;
    },
    [createJobProfile, intl.newJobProfileCreatedSuccessfully, showSuccess]
  );

  const remove = useCallback(
    async (uids: string[]) => {
      await Promise.all(uids.map((uid) => removeJobProfile({ variables: { uid } })));
      showSuccess(intl.jobProfileDeletedSuccessfully);
    },
    [intl.jobProfileDeletedSuccessfully, removeJobProfile, showSuccess]
  );

  return {
    create,
    update,
    remove,
    variables,
    setVariables,
    getProfiles,
    totalCount: jobProfiles?.jobProfiles.totalCount || 0,
    jobProfiles: jobProfiles ? jobProfiles.jobProfiles.jobProfiles : [],
    loading: createLoading || updateLoading || removeLoading || isGettingProfiles,
    refetch
  };
};

export const byCountryName = (a: CountryModel, b: CountryModel): number => a.name?.localeCompare(b.name);
